import { useGetStaticResource } from '@/services/static-resource';
import { toNum, toFixed, div, ROUND_DOWN } from '@/libs/decimal';
import { useCallback } from 'react';
import { getCurrency } from '@/utils/currency';
import Big from 'big.js';
import { useNormalizedLocale } from '@/hooks/use-normalized-locale';

/**
 * 计算 K/M/B/T 缩写的基数与后缀
 */
const getKMBTScale = (value: number): { baseValue: number; suffix: string; } => {
  const absVal = Math.abs(value);
  if (absVal >= 1_000_000_000_000) {
    return { baseValue: absVal / 1_000_000_000_000, suffix: 'T' };
  }
  if (absVal >= 1_000_000_000) {
    return { baseValue: absVal / 1_000_000_000, suffix: 'B' };
  }
  if (absVal >= 1_000_000) {
    return { baseValue: absVal / 1_000_000, suffix: 'M' };
  }
  if (absVal >= 1_000) {
    return { baseValue: absVal / 1_000, suffix: 'K' };
  }
  return { baseValue: absVal, suffix: '' };
};

/**
 * 计算 INR 货币的特殊缩写基数与后缀
 * [0, 1千) - 不需要特殊处理
 * [1千, 10万) - 转换为 K
 * [10万, 1千万) - 转换为 L
 * 1千万以上 - 转换为 Cr
 */
const getINRScale = (value: number): { baseValue: number; suffix: string; } => {
  const absVal = Math.abs(value);
  if (absVal >= 10_000_000) {
    // 1千万以上 - Cr (Crore)
    return { baseValue: absVal / 10_000_000, suffix: 'Cr' };
  }
  if (absVal >= 100_000) {
    // [10万, 1千万) - L (Lakh)
    return { baseValue: absVal / 100_000, suffix: 'L' };
  }
  if (absVal >= 1_000) {
    // [1千, 10万) - K (Thousand)
    return { baseValue: absVal / 1_000, suffix: 'K' };
  }
  // [0, 1千) - 不需要特殊处理
  return { baseValue: absVal, suffix: '' };
};

/**
 * 获取显示几位小数（公共方法）
 * - 整数: 0 位
 * - 1 位小数: 1 位
 * - >=2 位小数: 返回 maxDecimalPart（默认 2）
 */
export const getDecimalDisplay = (numStr: string | number, maxDecimalPart: number = 2) => {
  let bigNum;
  try {
    bigNum = Big(numStr);
  } catch (e) {
    console.log('e-------', e);
    return -1;
  }

  const s = bigNum.toString();
  const decimalIndex = s.indexOf('.');
  if (decimalIndex === -1) return 0;

  const decimalPartLength = s.length - 1 - decimalIndex;
  if (decimalPartLength <= 0) return 0;
  if (decimalPartLength === 1) return 1;
  return maxDecimalPart;
};

/**
 * 去掉字符串小数位后面连续的 0
 * 例如: "1.200" => "1.2", "1.00" => "1", "1.2300" => "1.23"
 * @param str 格式化的数字字符串
 * @returns 去掉末尾连续 0 后的字符串
 */
export const removeTrailingZeros = (str: string): string => {
  // 仅移除小数部分末尾的 0；纯整数不应受影响
  if (!str.includes('.')) return str;
  // 先移除小数部分末尾的连续 0（但保留前面的有效数字）
  let result = str.replace(/(\.\d*[1-9])0+$/, '$1');
  // 若小数部分全为 0，如 "1.00"，移除小数点与 0
  result = result.replace(/\.0+$/, '');
  // 防御性处理：若仍以小数点结尾，移除之
  result = result.replace(/\.$/, '');
  return result;
};

/**
 * Intl.NumberFormat 的兼容性封装
 */
const safeNumberFormat = (
  value: number,
  locale: string,
  options: Intl.NumberFormatOptions,
  ext?: {
    applyKMBT?: boolean; // 是否将数值按 K/M/B/T 或 INR 的 K/L/Cr 缩写显示
    currency?: string; // 货币代码，当为 INR 时使用 INR 的特殊缩写规则
    kmbtMaximumFractionDigits?: number; // 缩写后"基数"的小数位上限，若提供则覆盖 options.maximumFractionDigits
  }
): string => {
  try {
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      // 统一按向零截断（与 decimal.ts 的 ROUND_DOWN 一致）
      const { maximumFractionDigits = 0 } = options;

      if (ext?.applyKMBT) {
        const sign = value < 0 ? -1 : 1;
        // 当货币为 INR 时，使用 INR 的特殊缩放规则
        const { baseValue, suffix } = (ext.currency === 'INR' || locale === 'hi-IN') ? getINRScale(value) : getKMBTScale(value);
        const baseWithSign = baseValue * sign;
        const truncatedStr = toFixed(
          baseWithSign,
          ext.kmbtMaximumFractionDigits ?? maximumFractionDigits,
          ROUND_DOWN
        );
        const truncatedNum = Number(truncatedStr);
        const formatted = new Intl.NumberFormat(locale, {
          roundingMode: 'trunc' as unknown as Intl.NumberFormatOptions['roundingMode'],
          ...options,
        }).format(truncatedNum);
        return removeTrailingZeros(formatted) + suffix;
      }

      const truncatedStr = toFixed(value, maximumFractionDigits, ROUND_DOWN);
      const truncatedNum = Number(truncatedStr);
      const formatted = new Intl.NumberFormat(locale, {
        // 尝试使用标准的向零截断配置；若运行环境不支持，会被忽略
        roundingMode: 'trunc' as unknown as Intl.NumberFormatOptions['roundingMode'],
        ...options,
      }).format(truncatedNum);
      return removeTrailingZeros(formatted);
    }
  } catch (error) {
    console.warn('Intl.NumberFormat not supported, using fallback', error);
  }

  // 降级方案：手动格式化
  const { minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;
  const fixedDigits = Math.max(minimumFractionDigits, maximumFractionDigits);

  if (ext?.applyKMBT) {
    const sign = value < 0 ? '-' : '';
    // 当货币为 INR 时，使用 INR 的特殊缩放规则
    const { baseValue, suffix } = ext.currency === 'INR' ? getINRScale(value) : getKMBTScale(value);
    const formattedBase = toFixed(
      baseValue,
      ext.kmbtMaximumFractionDigits ?? fixedDigits,
      ROUND_DOWN
    );
    const [integerPartBase, decimalPartBase] = formattedBase.split('.');
    const withSeparatorBase = integerPartBase.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const base = decimalPartBase ? `${withSeparatorBase}.${decimalPartBase}` : withSeparatorBase;
    return removeTrailingZeros(sign + base) + suffix;
  }

  const formattedNum = toFixed(value, fixedDigits, ROUND_DOWN);
  // 添加千位分隔符
  const [integerPart, decimalPart] = formattedNum.split('.');
  const withSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const result = decimalPart ? `${withSeparator}.${decimalPart}` : withSeparator;
  return removeTrailingZeros(result);
};

/**
 * 顶部余额数值格式化 - MBT 缩写格式
 * 用于空间受限的 UI 显示
 */
export const useFormatBalanceDisplay = () => {
  const locale = useNormalizedLocale();

  return useCallback(({
    value,
    currency,
    defaultValue,
  }: {
    value: number;
    currency: string;
    defaultValue?: number;
  }): string => {
    const num = toNum(value);
    const isInteger = Number.isInteger(num);
    // 根据货币类型确定使用的 locale（INR 使用 hi-IN，其他使用传入的 locale）
    const finalLocale = currency === 'INR' ? 'hi-IN' : locale;

    if (defaultValue && defaultValue > 0 && defaultValue < 0.01) {
      return '<0.01';
    } else if (num < 100000) {
      // [0, 10万)
      if (isInteger) {
        const truncated = Math.floor(num);
        return safeNumberFormat(truncated, finalLocale, {
          useGrouping: true,
          maximumFractionDigits: 0,
          roundingMode: 'trunc',
        });
      }
      const truncatedStr = toFixed(num, 2, ROUND_DOWN);
      const truncatedNum = Number(truncatedStr);
      const formatted = safeNumberFormat(truncatedNum, finalLocale, {
        useGrouping: true,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        roundingMode: 'trunc',
      });
      return removeTrailingZeros(formatted);
    } else if (num < 10000000) {
      // [10万, 1000万) 只显示整数
      const truncated = Math.floor(num);
      return safeNumberFormat(truncated, finalLocale, {
        useGrouping: true,
        maximumFractionDigits: 0,
        roundingMode: 'trunc',
      });
    } else if (num < 1000000000) {
      // [1000万, 10亿) 显示 xxx(.xx)M
      const base = div(num, 1000000);
      const baseIsInteger = Number.isInteger(base);
      const digits = baseIsInteger ? 0 : getDecimalDisplay(base, 2);
      const formattedBase = safeNumberFormat(base, finalLocale, {
        useGrouping: true,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        roundingMode: 'trunc',
      });
      return removeTrailingZeros(formattedBase) + 'M';
    } else if (num < 1000000000000) {
      // [10亿, 1万亿) 显示 xxx(.xx)B
      const base = div(num, 1000000000);
      const baseIsInteger = Number.isInteger(base);
      const digits = baseIsInteger ? 0 : getDecimalDisplay(base, 2);
      const formattedBase = safeNumberFormat(base, finalLocale, {
        useGrouping: true,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        roundingMode: 'trunc',
      });
      return removeTrailingZeros(formattedBase) + 'B';
    } else {
      // [1万亿, 1千万亿) 显示 xxx(.xx)T
      const base = div(num, 1000000000000);
      const baseIsInteger = Number.isInteger(base);
      const digits = baseIsInteger ? 0 : getDecimalDisplay(base, 2);
      const formattedBase = safeNumberFormat(base, finalLocale, {
        useGrouping: true,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        roundingMode: 'trunc',
      });
      return removeTrailingZeros(formattedBase) + 'T';
    }
  }, [locale]);
};

/**
 * 顶部余额数值格式化 - MBT 缩写格式（向后兼容的导出）
 * @deprecated 请使用 useFormatBalanceDisplay hook
 */
export const formatBalanceDisplay = ({
  value,
  currency,
}: {
  value: number;
  currency: string;
}): string => {
  // 向后兼容，使用默认 locale
  const num = toNum(value);
  const isInteger = Number.isInteger(num);
  const finalLocale = currency === 'INR' ? 'hi-IN' : 'en';

  if (num < 100000) {
    if (isInteger) {
      const truncated = Math.floor(num);
      return safeNumberFormat(truncated, finalLocale, {
        useGrouping: true,
        maximumFractionDigits: 0,
        roundingMode: 'trunc',
      });
    }
    const truncatedStr = toFixed(num, 2, ROUND_DOWN);
    const truncatedNum = Number(truncatedStr);
    const formatted = safeNumberFormat(truncatedNum, finalLocale, {
      useGrouping: true,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      roundingMode: 'trunc',
    });
    return removeTrailingZeros(formatted);
  } else if (num < 10000000) {
    const truncated = Math.floor(num);
    return safeNumberFormat(truncated, finalLocale, {
      useGrouping: true,
      maximumFractionDigits: 0,
      roundingMode: 'trunc',
    });
  } else if (num < 1000000000) {
    const base = div(num, 1000000);
    const baseIsInteger = Number.isInteger(base);
    const digits = baseIsInteger ? 0 : getDecimalDisplay(base, 2);
    const formattedBase = safeNumberFormat(base, finalLocale, {
      useGrouping: true,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      roundingMode: 'trunc',
    });
    return formattedBase + 'M';
  } else if (num < 1000000000000) {
    const base = div(num, 1000000000);
    const baseIsInteger = Number.isInteger(base);
    const digits = baseIsInteger ? 0 : getDecimalDisplay(base, 2);
    const formattedBase = safeNumberFormat(base, finalLocale, {
      useGrouping: true,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      roundingMode: 'trunc',
    });
    return formattedBase + 'B';
  } else {
    const base = div(num, 1000000000000);
    const baseIsInteger = Number.isInteger(base);
    const digits = baseIsInteger ? 0 : getDecimalDisplay(base, 2);
    const formattedBase = safeNumberFormat(base, finalLocale, {
      useGrouping: true,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      roundingMode: 'trunc',
    });
    return formattedBase + 'T';
  }
};

/**
 * 通用 K/M/B/T 缩写格式化
 * - 规则参考 formatBalanceDisplay
 * - 默认最多保留 2 位小数并向零截断
 */
export const formatKMBT = (
  value: number | string,
  options?: {
    // 缩写后“基数”的小数位上限（优先用于 K/M/B/T 显示的基数部分）
    // 例如 1,234,567 => 1.23M，当设置为 1 时 => 1.2M
    maximumFractionDigits?: number;
  }
): string => {
  const num = toNum(value);
  const sign = num < 0 ? '-' : '';
  const { baseValue, suffix } = getKMBTScale(num);
  const isInteger = Number.isInteger(baseValue);
  const maxDigits = options?.maximumFractionDigits ?? 2;
  const digits = isInteger ? 0 : getDecimalDisplay(baseValue, maxDigits);
  const baseStr = toFixed(baseValue, digits, ROUND_DOWN);
  return removeTrailingZeros(sign + baseStr) + suffix;
};

/**
 * 余额格式化 Hook(顶部余额)
 */
export const useBalanceFormatter = () => {
  const locale = useNormalizedLocale();
  const { data: staticResource } = useGetStaticResource();

  /**
   * 余额详细格式化函数-（顶部余额）
   * 根据货币类型（法币/数字货币）和地区格式化显示
   * 数字货币显示4位小数，法币显示2位小数并带千位分隔符
   */
  const formatBalanceDetail = useCallback(
    (value: number | string, currency: string): string => {
      const num = toNum(value);
      const isInteger = Number.isInteger(num);
      // 判断是否是数字货币：没有 region 字段才是数字货币，有 region 的是法币
      const isCrypto = !staticResource?.currency?.[currency]?.region;

      // 整数时不展示小数位
      const toFixed = isInteger ? 0 : getDecimalDisplay(value, isCrypto ? 4 : 2);
      // 使用 Intl.NumberFormat 显示小数位数，带千位分隔符
      const formatted = safeNumberFormat(num, currency !== 'INR' ? locale : "hi-IN", {
        minimumFractionDigits: toFixed,
        maximumFractionDigits: toFixed,
        roundingMode: 'trunc',
        useGrouping: true,
      });
      return removeTrailingZeros(formatted);
    },
    [locale, staticResource]
  );

  /**
 * 余额详细格式化函数-（其它地方金额展示）
 * 若为整数，默认不显示小数
 * 若为小数，最多保留2位小数，如果最后1位小数是0，保留1位即可，大额缩写也遵循此规则，特殊场景可特殊设计
 */
  const formatCurrencyDetail = useCallback(
    (
      value: number | string,
      options?: {
        applyKMBT?: boolean; // 是否将数值按 K/M/B/T 或 INR 的 K/L/Cr 缩写显示
        currency?: string; // 货币代码，当为 INR 时使用 INR 的特殊缩写规则
        kmbtMaximumFractionDigits?: number;
      }
    ): string => {
      const num = toNum(value);
      if (num > 0 && num < 0.01 && !(options?.kmbtMaximumFractionDigits && options?.kmbtMaximumFractionDigits > 2)) return '<0.01';
      if (options?.kmbtMaximumFractionDigits && options?.kmbtMaximumFractionDigits > 2 && num > 0 && num < 0.0001) return '<0.0001';
      // 处理负数且保留两位小数后为 -0.00 的情况
      if (num < 0 && num > -0.01) return '-<0.01';

      // 计算展示小数位：当需要缩写时，基于缩写后的基数计算
      let digits = getDecimalDisplay(num, options?.kmbtMaximumFractionDigits ?? 2);
      if (options?.applyKMBT) {
        const sign = num < 0 ? -1 : 1;
        // 当货币为 INR 时，使用 INR 的特殊缩放规则
        const { baseValue } = options?.currency === 'INR' || locale === 'hi-IN' ? getINRScale(num) : getKMBTScale(num);
        const baseWithSign = baseValue * sign;
        digits = getDecimalDisplay(baseWithSign, options?.kmbtMaximumFractionDigits ?? 2);
      }
      const formatted = safeNumberFormat(
        num,
        locale,
        {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
          roundingMode: 'trunc',
          useGrouping: true,
        },
        {
          applyKMBT: options?.applyKMBT,
          currency: options?.currency || locale === 'hi-IN' ? 'INR' : options?.currency,
          kmbtMaximumFractionDigits: options?.kmbtMaximumFractionDigits,
        }
      );
      return removeTrailingZeros(formatted);
    },
    [locale, staticResource]
  );

  /**
   * @des 货币符号 + 货币格式化处理
   * @param value
   * @param currency
   * @returns
   */
  const formatCurrency = (value: number | string, currency: string) => {
    const amount = formatCurrencyDetail(value, { currency });
    const currencySymbol = getCurrency(currency);

    // 如果 amount 包含负号，负号要放在最前面
    if (amount.startsWith('-')) {
      // 负数：-₹100 或 -<₹0.01
      if (amount.startsWith('-<')) {
        return '-<' + currencySymbol + amount.slice(2);
      }
      return '-' + currencySymbol + amount.slice(1);
    }

    // 如果 amount 包含 <，则货币符号显示在 < 后面
    if (amount.startsWith('<')) {
      return '<' + currencySymbol + amount.slice(1);
    }

    return currencySymbol + amount;
  };

  /**
   * @des gullak-cash金额展示规则（不带货币符号）
   * 小于100：完整展示，最多显示2位小数，不要四舍五入，直接截断
   * 100-1000：只展示整数，小数直接截断
   * 1000到100000：缩写为K，最多展示1位小数，可调用getINRScale方法
   * 100000到10000000：缩写为L，最多1位小数，可调用getINRScale方法
   * 10000000以上：缩写为Cr，最多1位小数，可调用getINRScale方法
   */
  const formatGullakCash = useCallback(
    (value: number | string/* , currency: string */): string => {
      const num = toNum(value);

      // 处理负数和特殊小额
      if (num > 0 && num < 0.01) return '<0.01';
      if (num < 0 && num > -0.01) return '-<0.01';

      const absNum = Math.abs(num);
      const sign = num < 0 ? '-' : '';

      if (absNum < 100) {
        // 小于100：完整展示，最多显示2位小数，直接截断
        const digits = getDecimalDisplay(num, 2);
        const truncatedStr = toFixed(absNum, digits, ROUND_DOWN);
        const truncatedNum = Number(truncatedStr);
        const formatted = safeNumberFormat(truncatedNum, /* currency === 'INR' ? 'hi-IN' :  */locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
          roundingMode: 'trunc',
          useGrouping: true,
        });
        return sign + removeTrailingZeros(formatted);
      } else if (absNum < 1000) {
        // 100-1000：只展示整数，小数直接截断
        const truncated = Math.floor(absNum);
        const formatted = safeNumberFormat(truncated, /* currency === 'INR' ? 'hi-IN' :  */locale, {
          maximumFractionDigits: 0,
          roundingMode: 'trunc',
          useGrouping: true,
        });
        return sign + formatted;
      } else if (absNum < 100000) {
        // 1000到100000：缩写为K，最多展示1位小数
        const { baseValue, suffix } = getINRScale(absNum);
        const digits = getDecimalDisplay(baseValue, 1);
        const truncatedStr = toFixed(baseValue, digits, ROUND_DOWN);
        const truncatedNum = Number(truncatedStr);
        const formatted = safeNumberFormat(truncatedNum, /* currency === 'INR' ? 'hi-IN' :  */locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
          roundingMode: 'trunc',
          useGrouping: true,
        });
        return sign + removeTrailingZeros(formatted) + suffix;
      } else if (absNum < 10000000) {
        // 100000到10000000：缩写为L，最多1位小数
        const { baseValue, suffix } = getINRScale(absNum);
        const digits = getDecimalDisplay(baseValue, 1);
        const truncatedStr = toFixed(baseValue, digits, ROUND_DOWN);
        const truncatedNum = Number(truncatedStr);
        const formatted = safeNumberFormat(truncatedNum, /* currency === 'INR' ? 'hi-IN' :  */locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
          roundingMode: 'trunc',
          useGrouping: true,
        });
        return sign + removeTrailingZeros(formatted) + suffix;
      } else {
        // 10000000以上：缩写为Cr，最多1位小数
        const { baseValue, suffix } = getINRScale(absNum);
        const digits = getDecimalDisplay(baseValue, 1);
        const truncatedStr = toFixed(baseValue, digits, ROUND_DOWN);
        const truncatedNum = Number(truncatedStr);
        const formatted = safeNumberFormat(truncatedNum, /* currency === 'INR' ? 'hi-IN' :  */locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
          roundingMode: 'trunc',
          useGrouping: true,
        });
        return sign + removeTrailingZeros(formatted) + suffix;
      }
    },
    [locale]
  );

  return {
    formatBalanceDetail,
    formatCurrencyDetail,
    formatCurrency,
    formatGullakCash,
  };
};

// 1 USDT, 2 INR, 3 BRL
export const currencyTypes = {
  1: 'USDT',
  2: 'INR',
  3: 'BRL'
};